<script setup lang="ts">
import {
  NO_DIETARY_CLAIMS_TEXT,
  NO_DIETARY_INFO_TEXT,
  declaresNoDietaryClaims,
  productClaimsDietary,
} from "~/utils/effectiveAllergens";

interface Props {
  product?: Record<string, unknown>;
  showTooltip?: boolean;
  compact?: boolean;
  // Under a "Dietaries:" heading (ticket 1149, same as the hub's labels and print menus): text when
  // no badge renders. Off by default.
  showEmptyState?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  product: undefined,
  showTooltip: false,
  compact: false,
  showEmptyState: false,
});

const selected = ref("");

interface DietaryBadge {
  label: string;
  key: string;
  cssClass: string;
  tooltip: string;
}

const badges: DietaryBadge[] = [
  { label: "V", key: "is_vegetarian", cssClass: "veggie", tooltip: "Vegetarian" },
  { label: "VE", key: "is_vegan", cssClass: "vegan", tooltip: "Vegan" },
  { label: "H", key: "is_halal", cssClass: "halal", tooltip: "Halal" },
  { label: "NG", key: "is_gluten_free", cssClass: "gluten-free", tooltip: "No Gluten" },
  { label: "NN", key: "is_nut_free", cssClass: "nut-free", tooltip: "No Nuts" },
  { label: "ND", key: "is_dairy_free", cssClass: "dairy-free", tooltip: "No Dairy" },
];

// Raw flags (ticket 1149: the backend switches off contradicting claims on save). Same predicate
// the store's dietary filters use, so a badge and a filter can never disagree.
const activeBadges = computed(() =>
  badges.filter((badge) => productClaimsDietary(props.product, badge.key)),
);

const emptyStateText = computed(() =>
  declaresNoDietaryClaims(props.product) ? NO_DIETARY_CLAIMS_TEXT : NO_DIETARY_INFO_TEXT,
);
</script>

<template>
  <div :class="['product-allergens', { 'product-allergens--compact': compact }]">
    <div
      v-for="badge in activeBadges"
      :key="badge.key"
      :class="['product-allergens__attribute', badge.cssClass]"
      @mousedown="selected = badge.cssClass"
      @mouseover="selected = badge.cssClass"
      @mouseleave="selected = ''"
    >
      <p class="product-allergens__attribute--name">{{ badge.label }}</p>
      <p
        v-if="selected === badge.cssClass && showTooltip"
        class="product-allergens__attribute--tooltip"
      >
        {{ badge.tooltip }}
      </p>
    </div>
    <p
      v-if="showEmptyState && !activeBadges.length"
      class="product-allergens__empty grey-text small-text"
    >
      {{ emptyStateText }}
    </p>
  </div>
</template>

<style lang="scss">
// DietaryChip treatment — small rounded-square pill per variant,
// consistent with the Badge component but tinted by diet type.
.product-allergens {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;

  &--compact {
    .product-allergens__attribute {
      height: 16px;
      min-width: 16px;
      padding: 0 4px;

      &--name {
        font-size: 9px;
      }
    }
  }

  &__attribute {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    min-width: 20px;
    padding: 0 6px;
    border-radius: var(--radius-sm);
    background-color: var(--color-white-1);
    border: 1px solid var(--color-grey-3);
    color: var(--color-grey-1);
    cursor: pointer;
    font-family: "GalanoGrotesqueMedium", sans-serif !important;
    font-weight: var(--weight-semibold);
    position: relative;
    transition:
      background-color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);

    &--name {
      color: inherit;
      font-size: 11px;
      line-height: 1;
      margin: 0 !important;
      letter-spacing: 0.2px;
    }

    &--tooltip {
      position: absolute;
      top: calc(100% + var(--space-1));
      left: 50%;
      transform: translateX(-50%);
      padding: var(--space-1) var(--space-2);
      background-color: var(--color-yellow-2) !important;
      color: var(--color-grey-1) !important;
      border-radius: var(--radius-sm);
      text-align: center;
      z-index: 9999;
      box-shadow: var(--shadow-1);
      white-space: nowrap;
      font-size: 0.6rem !important;
      line-height: 0.8rem !important;
      margin: 0 !important;
      pointer-events: none;
    }
  }

  &__empty {
    margin: 0;
  }
}

// Dietary chips: low-saturation pastels only. Pink is reserved for CTAs and
// must never appear here — H and NN previously borrowed primary pink.
.veggie,
.vegan {
  background-color: var(--color-green-3) !important;
  border-color: transparent !important;
  color: var(--color-leaf-1) !important;
}

.halal {
  background-color: var(--color-rose-2) !important;
  border-color: transparent !important;
  color: var(--color-rose-1) !important;
}

.gluten-free {
  background-color: var(--color-amber-2) !important;
  border-color: transparent !important;
  color: var(--color-amber-1) !important;
}

.nut-free {
  background-color: var(--color-nut-2) !important;
  border-color: transparent !important;
  color: var(--color-nut-1) !important;
}

.dairy-free {
  background-color: var(--color-wheat-2) !important;
  border-color: transparent !important;
  color: var(--color-wheat-1) !important;

  p {
    color: var(--color-wheat-1);
  }
}
</style>
